import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const NEXT = { draft: ["validated"], validated: ["approved", "draft"], approved: ["published", "draft"], published: ["retired"] };

export default function AdminConfigurations() {
  const { tenantId } = useAuth();
  const [packages, setPackages] = useState([]);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [verOpen, setVerOpen] = useState(null);
  const [pkgForm, setPkgForm] = useState({ name: "", framework: "panchatanthra", kind: "questionnaire", description: "" });
  const [verForm, setVerForm] = useState({ label: "", notes: "", payload: "{\n  \"weighting\": \"equal\"\n}" });
  const [compare, setCompare] = useState(null);

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/config-packages`).then(({ data }) => setPackages(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const createPkg = async () => {
    try {
      await api.post(`/tenants/${tenantId}/config-packages`, pkgForm);
      toast.success("Configuration package created");
      setPkgOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createVersion = async (pkgId) => {
    let payload = {};
    try {
      payload = JSON.parse(verForm.payload || "{}");
    } catch {
      return toast.error("Payload must be valid JSON");
    }
    try {
      await api.post(`/tenants/${tenantId}/config-packages/${pkgId}/versions`, { ...verForm, payload });
      toast.success("Draft version created");
      setVerOpen(null);
      setVerForm({ label: "", notes: "", payload: "{}" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const transition = async (versionId, state) => {
    try {
      await api.post(`/tenants/${tenantId}/config-versions/${versionId}/transition`, { state });
      toast.success(`Moved to ${state}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const runCompare = async (a, b) => {
    try {
      const { data } = await api.get(`/tenants/${tenantId}/config-versions/compare`, { params: { a, b } });
      setCompare(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-configurations-header"
        eyebrow="Administration"
        title="Configuration version manager"
        description="Draft → validate → approve → publish. Published versions are immutable; in-flight cycles migrate only by explicit admin action."
        actions={
          <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-package-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">New package</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-display text-2xl">Configuration package</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps">Name</Label>
                  <Input data-testid="package-name-input" className="rounded-none" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Framework</Label>
                  <Select value={pkgForm.framework} onValueChange={(v) => setPkgForm({ ...pkgForm, framework: v })}>
                    <SelectTrigger data-testid="package-framework-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="panchatanthra">panchatanthra</SelectItem>
                      <SelectItem value="viveka_kartha">viveka_kartha</SelectItem>
                      <SelectItem value="dinacharya">dinacharya</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Kind</Label>
                  <Select value={pkgForm.kind} onValueChange={(v) => setPkgForm({ ...pkgForm, kind: v })}>
                    <SelectTrigger data-testid="package-kind-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["questionnaire", "scoring", "cadence", "dashboard", "report_template"].map((k) => (
                        <SelectItem key={k} value={k}>{k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="create-package-submit" className="w-full rounded-none" disabled={!pkgForm.name} onClick={createPkg}>Create package</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="space-y-6" data-testid="config-packages-list">
        {packages.map((p) => (
          <div key={p.id} className="panel">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div>
                <p className="font-display text-xl">{p.name}</p>
                <p className="label-caps mt-1">{p.framework} · {p.kind}</p>
              </div>
              <div className="flex items-center gap-2">
                {p.versions?.length >= 2 && (
                  <Button
                    data-testid={`compare-versions-${p.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                    onClick={() => runCompare(p.versions[1].id, p.versions[0].id)}
                  >
                    Compare latest two
                  </Button>
                )}
                <Dialog open={verOpen === p.id} onOpenChange={(v) => setVerOpen(v ? p.id : null)}>
                  <DialogTrigger asChild>
                    <Button data-testid={`new-version-${p.id}`} size="sm" className="rounded-none text-[10px] uppercase tracking-[0.15em]">New version</Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none">
                    <DialogHeader><DialogTitle className="font-display text-2xl">Draft version</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="label-caps">Label</Label>
                        <Input data-testid="version-label-input" className="rounded-none" value={verForm.label} onChange={(e) => setVerForm({ ...verForm, label: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="label-caps">Payload (JSON)</Label>
                        <Textarea data-testid="version-payload-input" rows={6} className="rounded-none font-mono text-xs" value={verForm.payload} onChange={(e) => setVerForm({ ...verForm, payload: e.target.value })} />
                      </div>
                      <Button data-testid="create-version-submit" className="w-full rounded-none" disabled={!verForm.label} onClick={() => createVersion(p.id)}>Create draft</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="divide-y divide-border">
              {(p.versions || []).map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`config-version-${v.id}`}>
                  <div>
                    <p className="text-sm">{v.label}</p>
                    <p className="label-caps mt-1">{(v.history || []).map((h) => h.state).join(" → ")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StateBadge state={v.state} />
                    {(NEXT[v.state] || []).map((s) => (
                      <Button
                        key={s}
                        data-testid={`transition-${v.id}-${s}`}
                        size="sm"
                        variant="outline"
                        className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                        onClick={() => transition(v.id, s)}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
              {(p.versions || []).length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No versions yet.</p>}
            </div>
          </div>
        ))}
        {packages.length === 0 && <EmptyState testid="config-empty" title="No configuration packages" message="Create a package to version questionnaires, scoring or cadences." />}
      </div>

      {compare && (
        <div className="panel p-6" data-testid="config-compare">
          <p className="label-caps mb-4">Comparing {compare.a.label} → {compare.b.label}</p>
          <div className="divide-y divide-border">
            {compare.diff.map((d) => (
              <div key={d.key} className={`flex items-center justify-between gap-4 py-2 text-sm ${d.changed ? "text-warning" : ""}`}>
                <span className="font-mono text-xs">{d.key}</span>
                <span className="font-mono text-xs">{JSON.stringify(d.a)} → {JSON.stringify(d.b)}</span>
              </div>
            ))}
            {compare.diff.length === 0 && <p className="py-4 text-sm text-muted-foreground">Both payloads are empty.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
